// The analyst's tattoo screen (PRD-0032 UC-7, SC-027, SC-028, marks slices 05–06, ADR-0049).
//
// Both reads are audited: the server records one `mark.search` row before it answers, so a
// caller fetches each once per view and never refetches it in the background. The queue is one
// POST with the filters and an optional text over the marks' descriptions: the words never ride
// in a URL. Every change is audited before it is made (`mark.decision`, `identity.merge`); one
// that could not be recorded answers 503 `audit_unavailable`. The open leads of the case
// (ADR-0061: the pairs between two persons that wait for a decision or a merge) are a third
// audited read; their count answers two numbers, names nothing and is not audited, so a badge may
// poll it. The shapes mirror `routers/v1/schemas/case_humint_tattoos.py`. Imported by path, not
// via the barrel.
import type { CaseRole } from './cases'
import { apiClient } from './client'
import type { MarkFiltersDto } from './humint-mark-search'
import { humintPath } from './humint-path'
import type { EncounterPlaceDto, SubjectMarkDto, SubjectSummaryDto } from './humint-subject-page'

export type TattooQueueStatusDto = 'pending' | 'decided' | 'all'

/** The queue's filters, named as the marks list names them; an empty list does not narrow. */
export type TattooQueueFiltersDto = Required<Omit<MarkFiltersDto, 'pending'>> & {
  status: TattooQueueStatusDto
}

/** The queue's one read: the filters, and a text over the descriptions (null: none). */
export interface TattooQueueRequest extends TattooQueueFiltersDto {
  text: string | null
}

/** The encounter the mark was recorded in. */
export interface MarkEncounterDto {
  type: string
  at: string | null
  /** `name` is the place typed in; a place known only by its point has none. */
  place: EncounterPlaceDto | null
}

/** A mark as the tattoo screen shows it: the subject page's mark, its owner and encounter. */
export interface ReviewMarkDto extends SubjectMarkDto {
  subject_ref: string
  encounter: MarkEncounterDto | null
  /** Width / height of the crop the matched points are normalised to; null until the features step records it. */
  crop_aspect: number | null
}

export interface TattooMarkRowDto {
  mark: ReviewMarkDto
  owner: SubjectSummaryDto
}

export interface TattooQueueResponse {
  /** The `mark.search` audit row's correlation id. */
  search_id: string
  /** At most 500 marks. */
  items: TattooMarkRowDto[]
  /** Marks per region before the region filter. */
  region_counts: Record<string, number>
  /** Marks of the case with a suggestion waiting, whatever the filters. */
  pending_total: number
  /** The caller's role; a viewer decides nothing. */
  member_role: CaseRole
  /** With a text: the meaning-based half of the search did not run, only exact words matched. */
  degraded: boolean
}

export interface PairEvidenceDto {
  inliers: number
  ratio: number
  spread: number
  rotation_deg: number
  scale: number | null
  mirrored: boolean
  /** The band shown: the server shows a provisional `strong` as `possible`. */
  band: string
  provisional: boolean
  /** `[query x, query y, candidate x, candidate y]`, 0..1 of each crop. */
  points: number[][]
}

export interface ReviewPairDto {
  pair_id: string
  query_mark_id: string
  candidate_mark_id: string
  status: string
  motif_only: boolean
  /** Met in one encounter or in one photo: never offered a merge. */
  seen_together: boolean
  evidence: PairEvidenceDto
}

export interface ReviewCandidateDto {
  pair: ReviewPairDto
  candidate: TattooMarkRowDto
}

export interface TattooCandidatesResponse {
  search_id: string
  query: TattooMarkRowDto
  candidates: ReviewCandidateDto[]
}

export interface PairDecisionRequest {
  decision: 'same' | 'different'
  note?: string | null
}

/** A mark of the pair with its suggestions still waiting after the change. */
export interface MarkPendingDto {
  mark_id: string
  pending_candidates: number
}

/** The pair after a change, and the counts the change moved, as the server now counts them. */
export interface PairStateDto {
  pair_id: string
  status: string
  motif_only: boolean
  /** The pair's two marks. */
  marks: MarkPendingDto[]
  /** Marks of the case with a suggestion waiting (the queue's `pending_total`). */
  pending_total: number
}

/** Where an open lead stands: waiting for same / different, or confirmed and waiting for a merge. */
export type TattooLeadStateDto = 'to_review' | 'to_merge'

/** One side of a lead: the mark, its owner and the small picture of the mark's close-up. */
export interface TattooLeadSideDto extends TattooMarkRowDto {
  /**
   * The close-up's thumbnail as a data URI (`data:image/jpeg;base64,…`), part of this read and
   * drawn as it is, never fetched; null when the mark has none.
   */
  thumbnail: string | null
}

/** A pair of marks on two different persons of the case that waits for an analyst. */
export interface TattooLeadRowDto {
  pair_id: string
  state: TattooLeadStateDto
  /** The band as the candidates read shows it (a provisional `strong` as `possible`). */
  band: string
  inliers: number
  seen_together: boolean
  created_at: string | null
  decided_at: string | null
  /** Exactly two, the lower mark id first. */
  sides: TattooLeadSideDto[]
}

export interface TattooLeadsResponse {
  /** The `mark.search` audit row's correlation id. */
  search_id: string
  /** At most 200: the ones to merge first, then the most matched points, then the newest. */
  items: TattooLeadRowDto[]
  /** Every open lead of the case. */
  total: number
  member_role: CaseRole
}

/** The open leads of the case as two numbers: no person, mark or pair is named. */
export interface TattooLeadCountDto {
  to_review: number
  to_merge: number
}

const pairPath = (caseId: string, pairId: string, action: string) =>
  humintPath(caseId, 'mark-matches', pairId, action)

export const humintTattoosApi = {
  /** The queue for the filters and the text (in the body, never a URL). Records one `mark.search`. */
  queue: (caseId: string, body: TattooQueueRequest, signal?: AbortSignal) =>
    apiClient.post<TattooQueueResponse>(humintPath(caseId, 'tattoos', 'queue'), body, { signal }),

  /** A mark with every pair it is in. Records one `mark.search`. */
  candidates: (caseId: string, markId: string, signal?: AbortSignal) =>
    apiClient.get<TattooCandidatesResponse>(humintPath(caseId, 'marks', markId, 'candidates'), {
      signal,
    }),

  /** The open leads of the case, each with both marks and their owners. Records one `mark.search`. */
  leads: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<TattooLeadsResponse>(humintPath(caseId, 'tattoos', 'leads'), { signal }),

  /** How many leads are open. Not audited: it names nothing, so a badge may poll it. */
  leadsCount: (caseId: string, signal?: AbortSignal) =>
    apiClient.get<TattooLeadCountDto>(humintPath(caseId, 'tattoos', 'leads', 'count'), { signal }),

  decide: (caseId: string, pairId: string, body: PairDecisionRequest) =>
    apiClient.post<PairStateDto>(pairPath(caseId, pairId, 'decision'), body),

  reopen: (caseId: string, pairId: string) =>
    apiClient.post<PairStateDto>(pairPath(caseId, pairId, 'reopen')),

  motifOnly: (caseId: string, pairId: string) =>
    apiClient.post<PairStateDto>(pairPath(caseId, pairId, 'motif-only')),

  /** Fold the other mark's subject into the subject of `survivorMarkId`; undone from entity resolution. */
  merge: (caseId: string, pairId: string, survivorMarkId: string) =>
    apiClient.post<{ survivor_ref: string }>(pairPath(caseId, pairId, 'merge'), {
      survivor_mark_id: survivorMarkId,
    }),
}
